<template>
  <div class="row">
    <div class="col-12">
      <div class="form-group">
        <div class="form-group--inside">
          <h4>General LLM settings</h4>
          <div class="description">Here, you can set the name and description of your LLM so you identify the model across all your projects and bots.</div>

          <div class="span" v-if="!this.newLLM">
            <h4 class="mt">
              Status
              <Tooltip title="Setting if the LLM is trained, if not trained the LLM won't be able to respond to messages. Press train LLM to train the LLM." />
            </h4>
            <div class="form-group--inside">
              <div class="dot-wrapper">
                <div class="dot" :class="isComplete ? 'green' : 'red'" />
                <div class="description trained">RAG {{ isComplete ? '' : 'not' }} trained</div>
              </div>
            </div>
          </div>

          <div :class="`col-12 row`">
            <div :class="`col-4`">
              <h4 class="mt">
                Provider
                <Tooltip title="Overview the provider of the LLM. This can be changed once the LLM is created. Select the provider from the dropdown." />
              </h4>

              <div class="form-group--inside">
                <Dropdown :items="providers" :value="getProvider" @input="setProvider($event)" />
              </div>
            </div>
          </div>

          <div :class="`col-12 row`">
            <div :class="`col-4`">
              <h4 class="mt">
                Model
                <Tooltip
                  title="Set the model of the LLM. Some models are faster, some are more accurate. If you want to change the model, select the new model from the dropdown."
                />
              </h4>
              <div class="form-group--inside">
                <Dropdown :items="models" :value="selectedModel" @input="setModel($event)" />
              </div>
            </div>
            <div :class="`col-4`">
              <h4 class="mt">
                Region
                <Tooltip
                  title="Set the region of the LLM. This will be used to store the LLM data in the correct region. If you want to change the region select the new region from the dropdown."
                />
              </h4>
              <div class="form-group--inside">
                <Dropdown :items="regions" :value="selectedRegion" @input="setRegion($event)" />
              </div>
            </div>
            <div :class="`col-4`">
              <h4 class="mt">
                Temperature
                <Tooltip
                  title="Set the temperature of the LLM. This will be used to control the randomness of the LLM's responses. Temperature ranges from 0.0 to 1.0, where 0.0 gives deterministic, consistent responses and 1.0 gives more random, creative responses. Default is 0.7."
                />
              </h4>
              <div class="form-group--inside">
                <Dropdown :items="temperatureOptions" :value="selectedTemperature" @input="setTemperature($event)" :noneValue="0.0" />
              </div>
            </div>
          </div>

          <h4 class="mt">
            Display name <Tooltip title="Set the display name of the LLM. This will be used to identify the LLM across all your projects and bots." />
          </h4>
          <Autocomplete :items="variables" id="botName" v-model="name" placeholder="Enter your LLM's name" :rows="1" spellcheck="false" />

          <h4 class="mt">
            Description <Tooltip title="Set the description of the LLM. This will be used to identify the LLM across all your projects and bots." />
          </h4>
          <div class="form-group--inside">
            <Autocomplete :items="variables" name="desc" id="desc" v-model="description" :rows="1" placeholder="Enter a description for your LLM" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import { LLM, LLMObject } from '../../../../../../common/types/llm-type'
import Autocomplete from '@/components/helpers/Autocomplete.vue'
import Dropdown from '@/components/helpers/Dropdown.vue'
import { LlmProviderEnum } from '@common/enums/bot/llm/llm-provider.enum'

import { OpenAIModelsEnabled, OpenAIEmbeddingsModelsEnabled } from '../../../../../../common/enums/open-ai-models'
import { AnthropicModelsEnabled } from '../../../../../../common/enums/anthropic/claude-models.enum'
import { AliModelsEnabled } from '../../../../../../common/enums/ali/ali.models.enum'
import { MistralModelsEnabled } from '../../../../../../common/enums/mistral/mistral-enum'
import { GoogleModelsEnabled } from '../../../../../../common/enums/google/google-modals.enum'
import { MetaModelsEnabled } from '../../../../../../common/enums/meta/meta-llama-enum'
import { MicrosoftModelsEnabled } from '../../../../../../common/enums/microsoft/phi-model-enum'
import { NomicEmbeddingsModelsEnabled } from '../../../../../../common/enums/nomic/embeddings-model.enum'
import { DeepseekModelsEnabled } from '../../../../../../common/enums/deepseek/deepseek-models-enum'
import { LlmRegion } from '@common/enums/bot/llm/llm-region.enum'
import Tooltip from '@/components/helpers/Tooltip.vue'

@Component({ components: { Autocomplete, Dropdown, Tooltip } })
export default class LLMdetails extends Vue {
  @Prop() newLLM: LLM
  @Prop() LLM: LLMObject
  @Prop() variables: string[]

  openAiModels = OpenAIModelsEnabled
  anthropicModels = AnthropicModelsEnabled
  ailModels = AliModelsEnabled
  mistralModels = MistralModelsEnabled
  googleModels = GoogleModelsEnabled
  metaModels = MetaModelsEnabled
  claudeModels = AnthropicModelsEnabled
  microsoftModels = MicrosoftModelsEnabled
  nomicModels = NomicEmbeddingsModelsEnabled
  openAiEmbeddingsModels = OpenAIEmbeddingsModelsEnabled
  deepseekModels = DeepseekModelsEnabled

  temperatureOptions = [0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]

  selectedModel:
    | 'default'
    | typeof OpenAIModelsEnabled
    | typeof GoogleModelsEnabled
    | typeof AnthropicModelsEnabled
    | typeof AliModelsEnabled
    | typeof MistralModelsEnabled
    | typeof MetaModelsEnabled
    | typeof DeepseekModelsEnabled
    | typeof OpenAIModelsEnabled
    | string = 'default'
  selectedEmbeddingsModel: typeof OpenAIEmbeddingsModelsEnabled | typeof NomicEmbeddingsModelsEnabled | string = 'default'

  selectedRegion = LlmRegion.EU
  selectedTemperature = 0.7

  providers = [
    LlmProviderEnum.OpenAI,
    LlmProviderEnum.Anthropic,
    LlmProviderEnum.Alibaba,
    LlmProviderEnum.Mistral,
    LlmProviderEnum.Google,
    LlmProviderEnum.Meta,
    LlmProviderEnum.Deepseek
  ]

  mounted() {
    if (this.LLM) {
      if (this.selectedModel) this.selectedModel = this.LLM.model
      if (this.selectedEmbeddingsModel) this.selectedEmbeddingsModel = this.LLM.embeddingModel
      if (this.selectedRegion) this.selectedRegion = this.LLM.region
      if (this.selectedTemperature) this.selectedTemperature = this.LLM.temperature
    }
  }

  get regions() {
    return Object.values(LlmRegion)
  }

  setRegion(event) {
    if (!event) return
    const region = LlmRegion[event]
    this.selectedRegion = region
    if (this.newLLM) {
      this.newLLM.region = region
    } else {
      this.LLM.region = region
    }
  }

  setProvider(event) {
    if (!event) return

    if (this.newLLM) {
      this.newLLM.llmProvider = event
      this.selectedModel = 'default'
      this.newLLM.model = 'default'
    } else {
      this.LLM.llmProvider = event
      this.selectedModel = 'default'
      this.LLM.model = 'default'
    }
  }

  get models() {
    if (this.getProvider === LlmProviderEnum.OpenAI) {
      return ['default', ...this.openAiModels]
    }
    if (this.getProvider === LlmProviderEnum.Anthropic) {
      return ['default', ...this.anthropicModels]
    }
    if (this.getProvider === LlmProviderEnum.Alibaba) {
      return ['default', ...this.ailModels]
    }
    if (this.getProvider === LlmProviderEnum.Mistral) {
      return ['default', ...this.mistralModels]
    }
    if (this.getProvider === LlmProviderEnum.Google) {
      return ['default', ...this.googleModels]
    }
    if (this.getProvider === LlmProviderEnum.Meta) {
      return ['default', ...this.metaModels]
    }
    if (this.getProvider === LlmProviderEnum.Deepseek) {
      return ['default', ...this.deepseekModels]
    }
  }

  get isComplete() {
    return this.newLLM ? false : this.LLM.isComplete
  }

  get name() {
    return this.newLLM ? this.newLLM.llmDisplayName : this.LLM.llmDisplayName
  }

  set name(value: string) {
    if (this.newLLM) {
      this.newLLM.llmDisplayName = value
    } else {
      this.LLM.llmDisplayName = value
    }
  }

  get description() {
    return this.newLLM ? this.newLLM.description : this.LLM.description
  }

  set description(value: string) {
    if (this.newLLM) {
      this.newLLM.description = value
    } else {
      this.LLM.description = value
    }
  }

  get getProvider() {
    return this.newLLM ? this.newLLM.llmProvider : this.LLM.llmProvider
  }

  get getModel() {
    return this.newLLM ? this.newLLM.model : this.LLM.model
  }

  setTemperature(event) {
    if (event === undefined || event === null) return
    this.selectedTemperature = event
    if (this.newLLM) {
      this.newLLM.temperature = event
    } else {
      this.LLM.temperature = event
    }
  }

  setModel(event) {
    if (!event) return
    this.selectedModel = event
    if (this.newLLM) {
      this.newLLM.model = event
    } else {
      this.LLM.model = event
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/scss/variables';

.description {
  font-size: 14px;
  margin-bottom: 15px;
}

.mt {
  margin-top: 20px;
}

.dot-wrapper {
  display: flex;
  margin: 0px 0px -15px 0px;
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;

    &.red {
      background-color: $red;
    }

    &.green {
      background-color: $green;
    }
  }
  .description {
    margin-left: 10px;
    margin-top: -5px;
  }
}
</style>
